import axios from 'axios'
import Papa from 'papaparse'

const SHEET_ID = process.env.VUE_APP_SHEET_ID

function parseDate(str) {
    if (!str) return null
    const dot = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
    if (dot) return new Date(+dot[3], +dot[2] - 1, +dot[1])
    const dash = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
    if (dash) return new Date(+dash[1], +dash[2] - 1, +dash[3])
    const d = new Date(str)
    return isNaN(d.getTime()) ? null : d
}

function parseBool(val) {
    if (val === true || val === 1) return true
    if (!val) return false
    const s = String(val).trim().toLowerCase()
    return s === 'true' || s === '1' || s === 'yes' || s === 'да'
}

function toDirectImageUrl(url) {
    if (!url) return ''
    url = String(url).trim()
    const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/)
    if (driveMatch) return `https://lh3.googleusercontent.com/d/${driveMatch[1]}`
    const driveOpen = url.match(/drive\.google\.com\/open\?id=([^&]+)/)
    if (driveOpen) return `https://lh3.googleusercontent.com/d/${driveOpen[1]}`
    const driveUc = url.match(/drive\.google\.com\/uc\?.*id=([^&]+)/)
    if (driveUc) return `https://lh3.googleusercontent.com/d/${driveUc[1]}`
    return url
}

const cache = {}
const CACHE_TTL = 5 * 60 * 1000

function getFromCache(sheetName) {
    const entry = cache[sheetName]
    if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
        return entry.data
    }
    return null
}

function setCache(sheetName, data) {
    cache[sheetName] = { data, timestamp: Date.now() }
}

async function fetchSheet(sheetName) {
    const cached = getFromCache(sheetName)
    if (cached) return cached

    const url = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:csv&headers=1&sheet=${encodeURIComponent(sheetName)}`

    try {
        const response = await axios.get(url, { responseType: 'text' })
        const text = (response.data || '').trim()

        if (!text || text.startsWith('<!') || text.startsWith('<html')) {
            console.warn(`[Sheets] "${sheetName}": got HTML instead of CSV`)
            return []
        }

        const result = Papa.parse(text, {
            header: true,
            skipEmptyLines: true,
            dynamicTyping: false,
            transformHeader: (h) => h.trim().toLowerCase().replace(/\s+/g, '_'),
        })

        if (!result.data || result.data.length === 0) {
            return []
        }

        setCache(sheetName, result.data)
        return result.data
    } catch (error) {
        console.warn(`[Sheets] Failed to fetch "${sheetName}":`, error.message)
        return []
    }
}

export async function fetchPaintings() {
    const rows = await fetchSheet('paintings')
    return rows.map(row => ({
        id: String(row.id || ''),
        title: String(row.title || ''),
        year: String(row.year || ''),
        medium: String(row.medium || ''),
        dimensions: String(row.dimensions || ''),
        tags: String(row.tags || '').split(',').map(t => t.trim()).filter(Boolean),
        imageUrl: toDirectImageUrl(row.image_url),
        thumbnailUrl: toDirectImageUrl(row.thumbnail_url || row.image_url),
        price: String(row.price || ''),
        isAvailable: parseBool(row.is_available),
        description: String(row.description || ''),
        isFeatured: parseBool(row.is_featured),
        sortOrder: parseInt(row.sort_order, 10) || 999,
        isSold: parseBool(row.sold),
    })).sort((a, b) => a.sortOrder - b.sortOrder)
}

export async function fetchExhibitions() {
    const rows = await fetchSheet('exhibitions')

    return rows.map(row => {
        const isRunning = parseBool(row.is_running)

        return {
            id: String(row.id || ''),
            title: String(row.title || ''),
            year: String(row.year || ''),
            location: String(row.location || ''),
            description: String(row.description || ''),
            imageUrl: toDirectImageUrl(row.image_url),
            type: String(row.type || 'personal').trim().toLowerCase(),
            status: isRunning ? 'current' : 'past',
        }
    })
}

export async function fetchTeaching() {
    const rows = await fetchSheet('teaching')
    return rows.map(row => ({
        id: String(row.id || ''),
        courseName: String(row.course_name || ''),
        description: String(row.description || ''),
        format: String(row.format || ''),
        price: String(row.price || ''),
        schedule: String(row.schedule || ''),
        isActive: parseBool(row.is_active),
    }))
}

export async function fetchBiography() {
    const rows = await fetchSheet('biography')
    return rows.map(row => ({
        id: String(row.id || ''),
        year: String(row.year || ''),
        title: String(row.title || ''),
        description: String(row.description || ''),
        sortOrder: parseInt(row.sort_order, 10) || 999,
    })).sort((a, b) => a.sortOrder - b.sortOrder)
}

export async function fetchSiteInfo() {
    const rows = await fetchSheet('info')
    const info = {}
    rows.forEach(row => {
        if (row.key) info[String(row.key).trim()] = String(row.value || '').trim()
    })
    return info
}

export function clearCache() {
    Object.keys(cache).forEach(key => delete cache[key])
}
